import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { forkJoin, of, switchMap } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DatePickerModule } from 'primeng/datepicker';
import { DrawerModule } from 'primeng/drawer';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';

import { Equipamento } from '@core/models/models';
import { FeedbackService } from '@core/services/feedback.service';

import {
  AnexoManutencaoEquipamento,
  ManutencaoEquipamento,
  ManutencaoEquipamentoForm,
} from '@shared/api/models/manutencao-equipamento.model';

import { ManutencaoEquipamentoService } from '@shared/api/services/manutencao-equipamento.service';

@Component({
  selector: 'app-equipamento-maintenance-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePickerModule,
    DrawerModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    SkeletonModule,
    TextareaModule,
    TooltipModule,
  ],
  templateUrl: './equipamento-maintenance-tab.component.html',
  styleUrl: './equipamento-maintenance-tab.component.scss',
})
export class EquipamentoMaintenanceTabComponent implements OnChanges {
  private readonly service = inject(ManutencaoEquipamentoService);
  private readonly feedback = inject(FeedbackService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  @Input() equipamentos: Equipamento[] = [];
  @Input() equipamentoId: number | null = null;
  @Input() tipoLabel = 'equipamentos';
  @Input() openOnInit = false;

  @Output() countChange = new EventEmitter<number>();

  readonly tipos = [
    { label: 'Revisão', value: 0 },
    { label: 'Reparação', value: 1 },
    { label: 'Calibração', value: 2 },
    { label: 'Inspecção', value: 3 },
  ];

  readonly estados = [
    { label: 'Em curso', value: 1 },
    { label: 'Planeada', value: 0 },
    { label: 'Concluída', value: 2 },
    { label: 'Cancelada', value: 3 },
  ];

  rows: ManutencaoEquipamento[] = [];
  loading = false;
  saving = false;
  savingConclusao = false;

  searchTerm = '';

  drawerVisible = false;
  editingId: number | null = null;

  // Conclusão Drawer
  concluirDrawerVisible = false;
  concluindoRow: ManutencaoEquipamento | null = null;
  concluirForm = {
    dataConclusao: new Date() as Date | null,
    custoReal: null as number | null,
    kmReal: null as number | null,
    oficina: '',
    observacoes: '',
  };
  concluirSelectedFiles: File[] = [];

  selectedFiles: File[] = [];
  submitted = false;

  readonly today = new Date();

  private autoOpened = false;

  formOficina = '';
  form = this.emptyForm();

  ngOnChanges(changes: SimpleChanges): void {
    if (this.openOnInit && !this.autoOpened) {
      this.autoOpened = true;
      queueMicrotask(() => this.openCreate());
    }

    if (changes['equipamentoId'] || changes['equipamentos']) {
      this.load();
    }
  }

  get currentEquipamento(): Equipamento | null {
    if (this.equipamentoId) {
      return (
        this.equipamentos.find((item) => item.id === this.equipamentoId) ??
        this.equipamentos[0] ??
        null
      );
    }
    return this.equipamentos.length === 1 ? this.equipamentos[0] : null;
  }

  // Verifica se um equipamento está inativo de forma flexível (booleano, enum ou string)
  isEquipamentoInativo(eq?: Equipamento | null): boolean {
    const target = eq ?? this.currentEquipamento;
    if (!target) return false;

    const anyEq = target as any;
    if (anyEq.ativo === false || anyEq.isAtivo === false) return true;

    const estado = String(anyEq.estado ?? anyEq.status ?? '')
      .trim()
      .toUpperCase();
    if (['INATIVO', 'INACTIVO', '3'].includes(estado)) return true;

    return false;
  }

  // Filtra equipamentos disponíveis para o formulário (apenas ativos)
  get availableEquipamentos(): Equipamento[] {
    let list = this.equipamentos.filter((item) => !!item.id && !this.isEquipamentoInativo(item));
    if (this.equipamentoId) {
      list = list.filter((item) => item.id === this.equipamentoId);
    }
    return list;
  }

  // ── Bloco 1: Última Revisão ──────────────────────────────────────
  get ultimaRevisao(): ManutencaoEquipamento | null {
    const concluidas = this.rows.filter((r) => r.manutencaoEstadoEnum === 2);
    if (!concluidas.length) return null;
    return concluidas
      .slice()
      .sort(
        (a, b) =>
          new Date(b.dataManutencao).getTime() -
          new Date(a.dataManutencao).getTime(),
      )[0];
  }

  // ── Bloco 2: Manutenções em Curso ────────────────────────────────
  get manutencoesEmCurso(): ManutencaoEquipamento[] {
    return this.rows.filter(
      (r) => r.manutencaoEstadoEnum === 1 || r.manutencaoEstadoEnum === 0,
    );
  }

  get proximaRevisaoTexto(): string {
    const eq = this.currentEquipamento;
    if (!eq) return '';
    const dataRev = eq.propriedades?.find(
      (p) => p.nome?.toUpperCase() === 'PROXIMA_REVISAO_DATA',
    )?.valor;
    const kmRev = eq.propriedades?.find(
      (p) => p.nome?.toUpperCase() === 'PROXIMA_REVISAO_KM',
    )?.valor;

    const parts: string[] = [];
    if (kmRev) parts.push(`${Number(kmRev).toLocaleString('pt-AO')} km`);
    if (dataRev) parts.push(this.formatDate(String(dataRev)));

    return parts.join(' ou ');
  }

  // ── Bloco 3: Resumo ──────────────────────────────────────────────
  get totalIntervencoes(): number {
    return this.rows.length;
  }

  get totalCost(): number {
    return this.rows.reduce(
      (total, row) => total + Number(row.custo || 0),
      0,
    );
  }

  get inProgressCount(): number {
    return this.manutencoesEmCurso.length;
  }

  // ── Bloco 4: Histórico Timeline ──────────────────────────────────
  get historicoConcluidas(): ManutencaoEquipamento[] {
    return this.rows
      .filter((r) => r.manutencaoEstadoEnum === 2)
      .sort(
        (a, b) =>
          new Date(b.dataManutencao).getTime() -
          new Date(a.dataManutencao).getTime(),
      );
  }

  get drawerTitle(): string {
    return this.editingId ? 'Editar manutenção' : 'Registar manutenção';
  }

  load(): void {
    if (this.equipamentoId === 0) {
      return;
    }

    this.loading = true;

    const filters = {
      pageNumber: 1,
      pageSize: 1000,
      ...(this.equipamentoId ? { EquipamentoId: this.equipamentoId } : {}),
    };

    this.service
      .listar(filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.rows = (response?.data ?? [])
            .map((item) => ({
              ...item,
              anexos: item.anexos ?? [],
              descricaoServico: item.descricaoServico ?? '',
              observacoes: item.observacoes ?? '',
            }))
            .sort(
              (a, b) =>
                new Date(b.dataManutencao).getTime() -
                new Date(a.dataManutencao).getTime(),
            );

          this.loading = false;
          this.emitCount();
        },
        error: (err) => {
          this.rows = [];
          this.loading = false;
          this.feedback.showError(err, 'Erro ao carregar manutenções.');
        },
      });
  }

  openCreate(): void {
    if (this.isEquipamentoInativo()) {
      this.feedback.showWarning('Não é possível agendar manutenções para um equipamento inativo.');
      return;
    }

    this.editingId = null;
    this.submitted = false;
    this.selectedFiles = [];
    this.formOficina = '';

    this.form = this.emptyForm();

    if (this.equipamentoId) {
      this.form.equipamentoId = this.equipamentoId;
    } else if (this.availableEquipamentos.length === 1) {
      this.form.equipamentoId = this.availableEquipamentos[0].id ?? null;
    }

    // Default to in progress
    this.form.manutencaoEstadoEnum = 1;
    this.drawerVisible = true;
  }

  openEdit(row: ManutencaoEquipamento): void {
    if (this.isEquipamentoInativo()) {
      this.feedback.showWarning('Não é possível editar manutenções de um equipamento inativo.');
      return;
    }

    this.editingId = row.id;
    this.submitted = false;
    this.selectedFiles = [];
    this.formOficina = this.getOficina(row);

    this.form = {
      equipamentoId: row.equipamentoId,
      dataManutencao: this.parseDate(row.dataManutencao),
      manutencaoTipoEnum: row.manutencaoTipoEnum,
      manutencaoEstadoEnum: row.manutencaoEstadoEnum,
      custo: row.custo,
      descricaoServico: row.descricaoServico ?? '',
      observacoes: this.getObservacoesLimpas(row),
      quilometragemOuHorasUso: row.quilometragemOuHorasUso,
    };

    this.drawerVisible = true;
  }

  closeDrawer(): void {
    if (this.saving) return;
    this.drawerVisible = false;
    this.submitted = false;
    this.selectedFiles = [];
  }

  save(): void {
    this.submitted = true;

    if (this.isEquipamentoInativo()) {
      this.feedback.showWarning('Não é possível guardar manutenções para um equipamento inativo.');
      return;
    }

    if (!this.isValid() || this.saving) {
      return;
    }

    const payload: ManutencaoEquipamentoForm = {
      ...this.form,
      observacoes: this.buildObservacoes(
        this.form.observacoes,
        this.formOficina,
      ),
    };

    this.saving = true;

    const op$ = this.editingId
      ? this.service.atualizar(this.editingId, payload)
      : this.service.criar(payload, this.selectedFiles);

    op$.pipe(
      switchMap((res) => {
        if (this.editingId && this.selectedFiles.length) {
          const uploads = this.selectedFiles.map((file) =>
            this.service.adicionarAnexo(this.editingId!, file, file.name),
          );
          return forkJoin(uploads).pipe(switchMap(() => of(res)));
        }
        return of(res);
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: () => {
        this.saving = false;
        this.drawerVisible = false;
        this.feedback.showSuccess(
          this.editingId
            ? 'Manutenção atualizada com sucesso.'
            : 'Manutenção registada com sucesso.',
        );
        this.load();
      },
      error: (err) => {
        this.saving = false;
        this.feedback.showError(err, 'Não foi possível guardar a manutenção.');
      },
    });
  }

  // ── Bloco 5: Fluxo de Conclusão ──────────────────────────────────
  openConcluir(row: ManutencaoEquipamento): void {
    if (this.isEquipamentoInativo()) {
      this.feedback.showWarning('Não é possível concluir manutenções para um equipamento inativo.');
      return;
    }

    this.concluindoRow = row;
    this.concluirSelectedFiles = [];
    this.concluirForm = {
      dataConclusao: new Date(),
      custoReal: row.custo || null,
      kmReal: row.quilometragemOuHorasUso || null,
      oficina: this.getOficina(row),
      observacoes: this.getObservacoesLimpas(row),
    };
    this.concluirDrawerVisible = true;
  }

  closeConcluirDrawer(): void {
    if (this.savingConclusao) return;
    this.concluirDrawerVisible = false;
    this.concluindoRow = null;
  }

  saveConclusao(): void {
    if (!this.concluindoRow) return;

    if (this.isEquipamentoInativo()) {
      this.feedback.showWarning('Não é possível alterar registos de um equipamento inativo.');
      return;
    }

    if (!this.concluirForm.dataConclusao) {
      this.feedback.showWarning('Informe a data de conclusão.');
      return;
    }

    this.savingConclusao = true;

    const payload: ManutencaoEquipamentoForm = {
      equipamentoId: this.concluindoRow.equipamentoId,
      dataManutencao: this.concluirForm.dataConclusao,
      manutencaoTipoEnum: this.concluindoRow.manutencaoTipoEnum,
      manutencaoEstadoEnum: 2, // Concluída
      custo: this.concluirForm.custoReal ?? 0,
      descricaoServico: this.concluindoRow.descricaoServico,
      observacoes: this.buildObservacoes(
        this.concluirForm.observacoes,
        this.concluirForm.oficina,
      ),
      quilometragemOuHorasUso: this.concluirForm.kmReal,
    };

    this.service
      .atualizar(this.concluindoRow.id, payload)
      .pipe(
        switchMap((res) => {
          if (this.concluirSelectedFiles.length) {
            const uploads = this.concluirSelectedFiles.map((file) =>
              this.service.adicionarAnexo(
                this.concluindoRow!.id,
                file,
                file.name,
              ),
            );
            return forkJoin(uploads).pipe(switchMap(() => of(res)));
          }
          return of(res);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.savingConclusao = false;
          this.concluirDrawerVisible = false;
          this.concluindoRow = null;
          this.feedback.showSuccess('Manutenção concluída com sucesso!');
          this.load();
        },
        error: (err) => {
          this.savingConclusao = false;
          this.feedback.showError(
            err,
            'Não foi possível concluir a manutenção.',
          );
        },
      });
  }

  confirmDelete(row: ManutencaoEquipamento): void {
    if (this.isEquipamentoInativo()) {
      this.feedback.showWarning('Não é possível eliminar manutenções de um equipamento inativo.');
      return;
    }

    this.confirmationService.confirm({
      header: 'Eliminar intervenção',
      message: `Tem a certeza que deseja eliminar esta manutenção de ${this.getTipoLabel(row.manutencaoTipoEnum)}?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.service
          .eliminar(row.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.feedback.showSuccess('Manutenção eliminada com sucesso.');
              this.load();
            },
            error: (err) => {
              this.feedback.showError(err, 'Erro ao eliminar manutenção.');
            },
          });
      },
    });
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.selectedFiles.push(...Array.from(input.files));
    input.value = '';
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  onConcluirFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.concluirSelectedFiles.push(...Array.from(input.files));
    input.value = '';
  }

  removeConcluirFile(index: number): void {
    this.concluirSelectedFiles.splice(index, 1);
  }

  // ── Helpers ──────────────────────────────────────────────────────
  getOficina(row?: ManutencaoEquipamento | null): string {
    if (!row || !row.observacoes) return '';
    const match = row.observacoes.match(/\[Oficina:\s*([^\]]+)\]/i);
    if (match) return match[1].trim();
    const match2 = row.observacoes.match(/Oficina:\s*([^\n\r,]+)/i);
    if (match2) return match2[1].trim();
    return '';
  }

  getObservacoesLimpas(row?: ManutencaoEquipamento | null): string {
    if (!row || !row.observacoes) return '';
    return row.observacoes
      .replace(/\[Oficina:\s*[^\]]+\]/gi, '')
      .replace(/Oficina:\s*[^\n\r,]+/gi, '')
      .trim();
  }

  private buildObservacoes(obs: string, oficina?: string): string {
    const clean = (obs || '').replace(/\[Oficina:\s*[^\]]+\]/gi, '').trim();
    if (oficina && oficina.trim()) {
      return `[Oficina: ${oficina.trim()}] ${clean}`.trim();
    }
    return clean;
  }

  getTipoLabel(value: number): string {
    return this.tipos.find((item) => item.value === value)?.label ?? 'Revisão';
  }

  getEstadoLabel(value: number): string {
    return (
      this.estados.find((item) => item.value === value)?.label ?? 'Em curso'
    );
  }

  getEstadoClass(value: number): string {
    switch (value) {
      case 0:
        return 'status-planeada';
      case 1:
        return 'status-em-curso';
      case 2:
        return 'status-concluida';
      case 3:
        return 'status-cancelada';
      default:
        return '';
    }
  }

  getTimelineTitle(row: ManutencaoEquipamento): string {
    const tipo = this.getTipoLabel(row.manutencaoTipoEnum);
    if (row.quilometragemOuHorasUso && row.manutencaoTipoEnum === 0) {
      return `Revisão dos ${Number(row.quilometragemOuHorasUso).toLocaleString('pt-AO')} km`;
    }
    if (row.descricaoServico) {
      return `${tipo} — ${row.descricaoServico}`;
    }
    return `${tipo} #${row.id}`;
  }

  formatDate(value?: string | Date | null): string {
    if (!value) return '—';
    try {
      const d = value instanceof Date ? value : new Date(value);
      return d.toLocaleDateString('pt-AO');
    } catch {
      return String(value);
    }
  }

  formatKz(value?: number | null): string {
    if (value === null || value === undefined) return '—';
    return (
      new Intl.NumberFormat('pt-AO', {
        maximumFractionDigits: 0,
      }).format(value) + ' Kz'
    );
  }

  private emptyForm(): ManutencaoEquipamentoForm {
    return {
      equipamentoId: null,
      dataManutencao: new Date(),
      manutencaoTipoEnum: 0,
      manutencaoEstadoEnum: 1,
      custo: null,
      descricaoServico: '',
      observacoes: '',
      quilometragemOuHorasUso: null,
    };
  }

  private parseDate(value?: string): Date | null {
    if (!value) return null;
    const [datePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    if (!year || !month || !day) return new Date(value);
    return new Date(year, month - 1, day);
  }

  private isValid(): boolean {
    return !!(
      this.form.equipamentoId &&
      this.form.dataManutencao &&
      this.form.manutencaoTipoEnum !== null &&
      this.form.manutencaoEstadoEnum !== null
    );
  }

  private emitCount(): void {
    this.countChange.emit(this.rows.length);
  }
}